<script setup lang="ts">

import {Loader2Icon} from "lucide-vue-next";
import {computed, type HTMLAttributes} from "vue";
import {ColorType} from "../../enums/ColorType";
import {OnlyBiggerThanBase, SizeType} from "../../enums/SizeType";
import {tw} from "../../utils/tailwind";

interface Props {
    color?: ColorType,
    size?: SizeType,
    ghost?: boolean,
    loading?: boolean,
    square?: boolean,
    class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
    color: ColorType.primary,
    size: SizeType.Base,
    ghost: false,
    loading: false,
    square: false,
})

const defaultStyles = `relative tracking-wide focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-colors duration-200 flex gap-2 justify-center items-center border`;
//

// @todo Maybe no computed here because color in most cases set on init and doesnt change. So we need to check the working without it later.
const colorClasses = computed(() => {
    const normalColor = `text-white bg-${props.color}-600 hover:bg-${props.color}-500 focus-visible:outline-${props.color}-600 border-${props.color}-600`;
    const ghostColor = `text-secondary-700 transition-colors duration-200 bg-transparent dark:hover:bg-${props.color}-600  hover:text-white hover:bg-${props.color}-600 dark:text-secondary-200 dark:border-secondary-700`;

    return tw(props.ghost ? ghostColor : normalColor, props.class);
})

const sizeClasses = computed(() => {
    let finalString = `text-${props.size}`

    const roundedSize = OnlyBiggerThanBase(props.size)
    finalString += ` rounded${roundedSize === SizeType.Base ? '' : '-' + roundedSize}`

    return tw(finalString, props.class);
})

const paddingClasses = computed(() => {

    if (props.square) return tw(`p-2 aspect-square`, props.class)

    switch (props.size) {
        case SizeType.ExtraSmall:
            return tw(`px-1.5 py-0.5`, props.class)
        case SizeType.Small:
            return tw(`px-4 py-1.5`, props.class)
        case SizeType.ExtraLarge:
        case SizeType.ExtraLarge2:
            return tw(`px-6 py-4`, props.class)
        case SizeType.Large:
        case SizeType.Base:
        default:
            return tw(`px-5 py-2`, props.class)
    }
})
</script>

<template>
    <button
        :class="[
            defaultStyles,
            colorClasses,
            sizeClasses,
            paddingClasses,
            {
                'text-white/0 dark:text-white/0 cursor-wait hover:none': loading,
            }
        ]"
        :disabled="loading"
    >
        <div class="absolute w-full h-full flex justify-center items-center text-white/90 dark:text-white/90" v-if="loading">
            <Loader2Icon class="w-5 h-5 animate-spin "/>
        </div>
        <slot/>
    </button>
</template>


